import { useState } from 'react';
import { toast } from 'react-toastify';
import { makeApiRequests } from '../helpers/api';
import { ENDPOINTS } from '../helpers/constants';
import { updateItemsInArray } from '../helpers/global';
import useAuth from './useAuth';
import { isCustomer } from '../helpers/global';

const useApartmentProfile = () => {
  const { user } = useAuth();
  const [customerProfileMeta, setCustomerProfileMeta] = useState(null);
  const [deleteApartmentProfileMeta, setDeleteApartmentProfileMeta] = useState(null);

  const handleAddProfileClick = (data, activeCustomer) => {
    const dealsOfActiveCustomer = activeCustomer?.deals;
    if (dealsOfActiveCustomer?.length === 1 && data.mode === 'add') {
      data.data.deal = dealsOfActiveCustomer[0]._id;
    }

    setCustomerProfileMeta(data);
  };

  const handleDeleteProfileClick = userProfile => {
    setDeleteApartmentProfileMeta({
      profileToDelete: userProfile
    });
  };

  const submitCustomerProfile = async (customerProfile, activeCustomer, onCustomerUpdate) => {
    setCustomerProfileMeta(prev => ({ ...prev, showProgress: true }));

    const profileToSave = {
      ...customerProfile,
      showToCustomer: customerProfile._id
        ? customerProfile.showToCustomer // Preserve existing value on update
        : isCustomer(user.role) // only set showToCustomer to true when customer creates a new profile
    };

    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.USERS_UPDATE,
      requestBody: {
        _id: activeCustomer._id,
        apartmentProfiles: updateItemsInArray(activeCustomer?.apartmentProfiles, profileToSave, '_id', false)
      }
    });

    setCustomerProfileMeta(prev => ({ ...prev, showProgress: false }));

    if (error) {
      toast.error(error);
      return;
    }

    setCustomerProfileMeta(null);
    onCustomerUpdate(response);
    toast.success('Profile saved successfully');
  };

  const deleteApartmentProfile = async (activeCustomer, onCustomerUpdate) => {
    try {
      const userProfileToDelete = deleteApartmentProfileMeta?.profileToDelete;
      setDeleteApartmentProfileMeta(prev => ({ ...prev, showProgress: true }));

      const updatedApartmentProfiles = activeCustomer?.apartmentProfiles?.filter(
        ap => ap?._id !== userProfileToDelete?._id
      );

      const { response, error } = await makeApiRequests({
        endpoint: ENDPOINTS.USERS_UPDATE,
        requestBody: {
          _id: activeCustomer._id,
          apartmentProfiles: updatedApartmentProfiles
        }
      });

      setDeleteApartmentProfileMeta(prev => ({ ...prev, showProgress: false }));
      setCustomerProfileMeta(null);

      if (error) {
        toast.error(error);
        return;
      }

      setDeleteApartmentProfileMeta(null);
      onCustomerUpdate(response);
      toast.success('Profile deleted successfully');
    } catch (error) {
      console.error('Error deleting apartment profile:', error);
      setDeleteApartmentProfileMeta(prev => ({ ...prev, showProgress: false }));
      toast.error('Failed to delete profile');
    }
  };

  const closeProfileModal = () => {
    setCustomerProfileMeta(null);
  };

  const closeDeleteModal = () => {
    setDeleteApartmentProfileMeta(null);
  };

  const toggleProfileApproval = async (profileToApprove, isApproved, activeCustomer, onCustomerUpdate) => {
    setCustomerProfileMeta(prev => ({ ...prev, showProgress: true }));

    // Map through all profiles and only update isApproved for the target profile
    const updatedProfiles = activeCustomer.apartmentProfiles.map(
      profile =>
        profile._id === profileToApprove._id
          ? { ...profile, isApproved: isApproved } // Keep all fields, update only isApproved
          : profile // Keep other profiles unchanged
    );

    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.USERS_UPDATE,
      requestBody: {
        _id: activeCustomer._id,
        apartmentProfiles: updatedProfiles
      }
    });

    setCustomerProfileMeta(prev => ({ ...prev, showProgress: false }));

    if (error) {
      toast.error(error);
      return;
    }

    // Update the current profile data in the modal as well to reflect changes
    setCustomerProfileMeta(prev => ({
      ...prev,
      data: { ...prev.data, isApproved: isApproved }
    }));

    onCustomerUpdate(response);
    toast.success(isApproved ? 'Profile approved successfully' : 'Profile unapproved successfully');
  };

  const publishProfileForReview = async (profileToPublish, activeCustomer, onCustomerUpdate) => {
    setCustomerProfileMeta(prev => ({ ...prev, showProgress: true }));

    // Map through all profiles and only update isPublishedForReview for the target profile
    const updatedProfiles = activeCustomer.apartmentProfiles.map(
      profile =>
        profile._id === profileToPublish._id
          ? { ...profile, isPublishedForReview: true } // Keep all fields, update only isPublishedForReview
          : profile // Keep other profiles unchanged
    );

    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.USERS_UPDATE,
      requestBody: {
        _id: activeCustomer._id,
        apartmentProfiles: updatedProfiles
      }
    });

    setCustomerProfileMeta(prev => ({ ...prev, showProgress: false }));

    if (error) {
      toast.error(error);
      return;
    }

    // Update the current profile data in the modal as well to reflect changes
    setCustomerProfileMeta(prev => ({
      ...prev,
      data: { ...prev.data, isPublishedForReview: true }
    }));

    onCustomerUpdate(response);
    toast.success('Profile published for review successfully');
  };

  return {
    customerProfileMeta,
    deleteApartmentProfileMeta,
    handleAddProfileClick,
    handleDeleteProfileClick,
    submitCustomerProfile,
    deleteApartmentProfile,
    toggleProfileApproval,
    publishProfileForReview,
    closeProfileModal,
    closeDeleteModal
  };
};

export default useApartmentProfile;
